import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import {
  SALVA_DISCIPLINA,
  ATUALIZA_DISCIPLINA,
  INICIA_DISCIPLINA,
  BUSCA_DISCIPLINA_EDICAO,
  REMOVE_DISCIPLINA,
  LISTA_DISCIPLINA,
  ON_HIDE_LOADER
} from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import { showAuthMessage } from "actions/Disciplina";
import {
  inicioDisciplinaSucesso,
  salvaDisciplinaSucesso,
  removeDisciplinaSucesso,
  buscaDisciplinaEdicaoSucesso,
  listaDisciplinaSucesso
} from "actions/Disciplina";

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'object') {
    const cand = v.id ?? v.value ?? v.valor;
    if (cand === undefined || cand === null) return null;
    return toNumberOrNull(cand);
  }
  return null;
};

const extractIdNumber = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'object') {
    if (typeof v.value === 'object') return toNumberOrNull(v.value.id ?? v.value.value ?? v.value);
    return toNumberOrNull(v.id ?? v.value ?? v);
  }
  return toNumberOrNull(v);
};

const normalizeDisciplinaPayload = (p) => {
  if (!p || typeof p !== 'object') return p;
  const out = {};

  out.Id = toNumberOrNull(p.Id ?? p.id);
  const nome = p.Nome ?? p.nome;
  const nomeAbreviado = p.NomeAbreviado ?? p.nomeAbreviado ?? p.abreviatura ?? p.Nome_Abreviado ?? p.nome_abreviado;
  const baseText = p.BaseCurricular ?? p.baseCurricular;
  const tipoBaseRaw = p.TipoBase ?? p.tipoBase ?? p.tipo_base;
  const codigoEduc = p.CodigoEducacenso ?? p.codigoEducacenso ?? p.codigo_educacenso;
  const ordemRaw = p.OrdemApresentacao ?? p.ordemApresentacao ?? p.ordem_apresentacao;
  const desconsideraRaw = p.DesconsideraParaProgressao ?? p.desconsideraParaProgressao ?? p.desconsidera_para_progressao;
  const areaRaw = p.AreaConhecimentoId ?? p.areaConhecimentoId ?? p.area_conhecimento_id;

  const mapTipoBaseToText = (tb) => {
    const n = toNumberOrNull(tb);
    if (n === 1) return 'Base nacional comum';
    if (n === 2) return 'Base diversificada';
    if (n === 3) return 'Base profissional';
    if (n === 4) return 'Art.33 (Ensino religioso)';
    if (typeof tb === 'string' && tb.trim().length > 0) return tb; // já é texto
    return '';
  };

  out.Nome = nome ?? '';
  out.NomeAbreviado = nomeAbreviado ?? '';
  out.BaseCurricular = (baseText && String(baseText).trim().length > 0) ? baseText : mapTipoBaseToText(tipoBaseRaw);
  out.CodigoEducacenso = codigoEduc ?? '';
  out.OrdemApresentacao = toNumberOrNull(ordemRaw);
  out.DesconsideraParaProgressao = !!desconsideraRaw;
  out.AreaConhecimentoId = extractIdNumber(areaRaw);
  return out;
};

const getDisciplinas = async () =>
  await requisicaoApi.get('/disciplinas')
    .then(resposta => resposta)
    .catch(error => error);

const getDisciplinaEdicao = async (id) =>
  await requisicaoApi.get('/disciplinas/' + id)
    .then(resposta => resposta)
    .catch(error => error);

const getListaDisciplina = async (nomeFiltro) =>
  await requisicaoApi.get('/disciplinas', { params: { Nome: (nomeFiltro || '') } })
    .then(resposta => resposta)
    .catch(error => error);

const adicionaDisciplinaRequest = async (disciplina) =>
  await requisicaoApi.post('/disciplinas', disciplina)
    .then(resposta => resposta)
    .catch(error => error);

const atualizaDisciplinaRequest = async (disciplina) =>
  await requisicaoApi.put('/disciplinas/' + (disciplina.Id ?? disciplina.id ?? ''), disciplina)
    .then(resposta => resposta)
    .catch(error => error);

const deleteDisciplinaRequest = async (id) =>
  await requisicaoApi.delete('/disciplinas/' + id)
    .then(resposta => resposta)
    .catch(error => error);

function* fnIniciaDisciplina() {
  try {
    const retorno = yield call(getDisciplinas);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(inicioDisciplinaSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnBuscaDisciplinaEdicao({ payload }) {
  try {
    const retorno = yield call(getDisciplinaEdicao, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(buscaDisciplinaEdicaoSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaDisciplina({ payload }) {
  try {
    const { nome } = payload;
    const retorno = yield call(getListaDisciplina, nome);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(listaDisciplinaSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnSalvaDisciplina({ payload }) {
  try {
    const payloadNormalized = normalizeDisciplinaPayload(payload);
    const retorno = yield call(adicionaDisciplinaRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaDisciplinaSucesso('Disciplina salva com sucesso'));
      yield put({ type: LISTA_DISCIPLINA, payload: { nome: '' } });
    }
  } catch (error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 400) {
      let mensagemValidacao = '';
      let inconsistencias = data?.inconsistencias || [];
      if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
        mensagemValidacao = inconsistencias[0].mensagem;
      } else if (data && typeof data === 'object') {
        mensagemValidacao = data.mensagem || JSON.stringify(data);
      }
      yield put(showAuthMessage(mensagemValidacao || (error.message ? JSON.stringify(error.message) : 'Erro de validação')));
    } else {
      yield put(showAuthMessage(error && error.message ? JSON.stringify(error.message) : JSON.stringify(error)));
    }
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnAtualizaDisciplina({ payload }) {
  try {
    const payloadNormalized = normalizeDisciplinaPayload(payload);
    const retorno = yield call(atualizaDisciplinaRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaDisciplinaSucesso('Disciplina atualizada com sucesso'));
      yield put({ type: LISTA_DISCIPLINA, payload: { nome: '' } });
    }
  } catch (error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 400) {
      let mensagemValidacao = '';
      let inconsistencias = data?.inconsistencias || [];
      if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
        mensagemValidacao = inconsistencias[0].mensagem;
      } else if (data && typeof data === 'object') {
        mensagemValidacao = data.mensagem || JSON.stringify(data);
      }
      yield put(showAuthMessage(mensagemValidacao || (error.message ? JSON.stringify(error.message) : 'Erro de validação')));
    } else {
      yield put(showAuthMessage(error && error.message ? JSON.stringify(error.message) : JSON.stringify(error)));
    }
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnRemoveDisciplina({ payload }) {
  try {
    const retorno = yield call(deleteDisciplinaRequest, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(removeDisciplinaSucesso('Disciplina removida com sucesso'));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

export function* iniciaAcoesDisciplina() { yield takeEvery(INICIA_DISCIPLINA, fnIniciaDisciplina); }
export function* salvaDisciplina() { yield takeEvery(SALVA_DISCIPLINA, fnSalvaDisciplina); }
export function* atualizaDisciplina() { yield takeEvery(ATUALIZA_DISCIPLINA, fnAtualizaDisciplina); }
export function* buscaDisciplinaEdicao() { yield takeEvery(BUSCA_DISCIPLINA_EDICAO, fnBuscaDisciplinaEdicao); }
export function* removeDisciplina() { yield takeEvery(REMOVE_DISCIPLINA, fnRemoveDisciplina); }
export function* listaDisciplina() { yield takeEvery(LISTA_DISCIPLINA, fnListaDisciplina); }

export default function* rootSaga() {
  yield all([
    fork(iniciaAcoesDisciplina),
    fork(salvaDisciplina),
    fork(atualizaDisciplina),
    fork(buscaDisciplinaEdicao),
    fork(removeDisciplina),
    fork(listaDisciplina),
  ]);
}
